import React, { ChangeEvent, useState } from 'react';
import { Stack, Wrap } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Input, Option, Select } from '@fiverr-private/forms';
import { ErrorCircleIcon } from '@fiverr-private/visuals';
import { AppI18n } from '../../../../i18n';
import {
  BudgetAndTimelineContent,
  formatNumberWithComma,
  getCommaFormatedStringAsNumber,
  MAX_RECOMMENDED_DELIVERY_FREQUENCY,
  numberRegex,
  TimeUnit,
  timeUnits,
  useBudgetAndTimelineContext,
} from '../utils';

type validationMessageType = 'invalid_value_error' | 'non_standard_but_valid_value_message';

export const DeliveriesFrequencySelector = () => {
  const { sectionContentState, setSectionContentState } = useBudgetAndTimelineContext();
  const { timelineDetails } = sectionContentState;
  const { deliveriesFrequency = {} } = timelineDetails ?? {};
  const [validationMessage, setValidationMessage] = useState<validationMessageType | null>(null);

  const onFrequencyValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = getCommaFormatedStringAsNumber(event.target.value).toString();

    if (!numberRegex.test(value)) {
      setValidationMessage('invalid_value_error');
      return;
    }

    const numericValue = Number(value);

    setValidationMessage(
      numericValue > MAX_RECOMMENDED_DELIVERY_FREQUENCY ? 'non_standard_but_valid_value_message' : null
    );

    setSectionContentState({
      ...sectionContentState,
      timelineDetails: {
        ...sectionContentState.timelineDetails,
        deliveriesFrequency: {
          ...sectionContentState.timelineDetails?.deliveriesFrequency,
          value: numericValue,
        },
      },
    });
  };

  const onFrequencyUnitChange = (unit: TimeUnit) => {
    setSectionContentState({
      ...sectionContentState,
      timelineDetails: {
        ...sectionContentState.timelineDetails,
        deliveriesFrequency: {
          ...sectionContentState.timelineDetails?.deliveriesFrequency,
          unit,
        },
      },
    });
  };

  return (
    <Stack direction="column">
      <Stack>
        <Text size="b_md" fontWeight="semibold">
          <AppI18n k={'sections.budget_and_timeline.deliveries_frequency.title'} />
        </Text>
      </Stack>
      <Wrap direction="row">
        <Stack>
          <Stack>
            <Input
              value={deliveriesFrequency.value ? formatNumberWithComma(deliveriesFrequency.value) : ''}
              onChange={onFrequencyValueChange}
              maxWidth="90px"
            />
          </Stack>
          <Stack alignItems="center">
            <Text>
              <AppI18n k={'sections.budget_and_timeline.deliveries_frequency.deliveries_per'} />
            </Text>
          </Stack>
        </Stack>
        <Stack width="170px">
          <Select
            value={{ item: deliveriesFrequency.unit }}
            onChange={(values) => onFrequencyUnitChange(values.item as TimeUnit)}
            fullWidth={true}
          >
            {Object.keys(timeUnits).map((unit) => (
              <Option key={unit} value={timeUnits[unit]}>
                <AppI18n k={`sections.budget_and_timeline.deliveries_frequency.${timeUnits[unit] as TimeUnit}`} />
              </Option>
            ))}
          </Select>
        </Stack>
      </Wrap>
      {!!validationMessage && (
        <Stack>
          <ValidationMessage validationMessage={validationMessage} />
        </Stack>
      )}
    </Stack>
  );
};

export const isInvalidFrequencyValue = (sectionContentState: BudgetAndTimelineContent) => {
  const { timelineDetails } = sectionContentState;
  const { deliveriesFrequency = {} } = timelineDetails ?? {};

  return !deliveriesFrequency.value || typeof deliveriesFrequency.value !== 'number' || deliveriesFrequency.value <= 0;
};

const ValidationMessage = ({ validationMessage }: { validationMessage: validationMessageType }) => {
  const messageCopy = `sections.budget_and_timeline.deliveries_frequency.${validationMessage}` as const;

  return validationMessage === 'invalid_value_error' ? (
    <Stack gap="1.5" justifyContent="center" alignItems="center">
      <ErrorCircleIcon color="red_1000" size="sm" />
      <Text size="b_sm" color="validationError">
        <AppI18n k={messageCopy} />
      </Text>
    </Stack>
  ) : (
    <Text size="b_sm">
      <AppI18n k={messageCopy} />
    </Text>
  );
};
